<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>&nbsp;
            <NevsButton class="error" @click="backClick"><i class="fa-solid fa-arrow-left"></i>
                {{ $LANG.Get('buttons.back') }}
            </NevsButton>
        </div>
        <NevsCard class="membership-form">
            <div class="membership-form-grid">
                <NevsTextField v-model="membership.name" :error="validation.name"
                               :label="'*' + $LANG.Get('fields.nameHr') + ':'"></NevsTextField>
                <NevsTextField v-model="membership.name_en"
                               :label="$LANG.Get('fields.nameEn') + ':'"></NevsTextField>
                <NevsSelect class="span-2" :minimumSearchLength="3" v-model="membership.code_kpd" :ajax="'select/product-codes'"
                            :label="$LANG.Get('fields.code_kpd') + ':'"></NevsSelect>
                <NevsNumberField :decimal-places="0" v-model="membership.duration"
                                 :label="$LANG.Get('fields.duration') + ':'"></NevsNumberField>
                <NevsSelect v-model="membership.duration_type" :options="durationTypeOptions"
                            :label="$LANG.Get('fields.durationType') + ':'"></NevsSelect>
                <NevsNumberField v-model="membership.price" :error="validation.price"
                                 :label="'*' + $LANG.Get('fields.priceWithoutTax').split('%currency%').join($HELPERS.GetDefaultCurrency()) + ':'"></NevsNumberField>
                <NevsNumberField v-show="$store.state.user.client.tax_system" v-model="membership.tax"
                                 :label="'*' + $LANG.Get('fields.tax') + ':'"></NevsNumberField>
                <NevsNumberField :decimal-places="0" v-model="membership.max_entries" :error="validation.max_entries"
                                 :hint="$LANG.Get('labels.maxEntriesHint')"
                                 :label="$LANG.Get('fields.maxEntries') + ':'"></NevsNumberField>
            </div>

            <div class="membership-form-flags">
                <NevsCheckbox v-model="membership.active" :label="$LANG.Get('fields.activeF')"></NevsCheckbox>
            </div>

            <div class="membership-form-sections">
                <div class="membership-form-section">
                    <h2>{{ $LANG.Get('labels.scheduleHeading') }}</h2>
                    <div class="schedule">
                        <div v-for="day in weekdays" :key="day.value" class="schedule-day">
                            <div class="schedule-day-name">{{ day.label }}</div>
                            <div class="schedule-day-body">
                                <div v-for="(interval, key) in membership.schedule[day.value]" :key="key" class="schedule-interval">
                                    <i class="fa-regular fa-clock schedule-interval-icon"></i>
                                    <NevsMaskedField v-model="interval.from" :mask="'NN:NN'" :width="'76px'"></NevsMaskedField>
                                    <span class="schedule-interval-dash">–</span>
                                    <NevsMaskedField v-model="interval.to" :mask="'NN:NN'" :width="'76px'"></NevsMaskedField>
                                    <span :title="$LANG.Get('tooltips.delete')" class="schedule-interval-remove"
                                          @click="removeInterval(day.value, key)"><i class="fa-solid fa-xmark"></i></span>
                                </div>
                                <span v-if="membership.schedule[day.value].length === 0" class="schedule-empty">
                                    {{ $LANG.Get('labels.noEntryAllowed') }}
                                </span>
                                <span class="schedule-add" @click="addInterval(day.value)">
                                    <i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addInterval') }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="membership-form-columns">
                <div class="membership-form-section">
                    <h2>{{ $LANG.Get('fields.products') }}</h2>
                    <table class="membership-products-table">
                        <thead>
                            <tr>
                                <th>{{ $LANG.Get('fields.product') }}</th>
                                <th style="width: 130px;">{{ $LANG.Get('fields.quantity') }}</th>
                                <th style="width: 20px;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(membershipProduct, key) in membership.products" :key="key">
                                <td><NevsSelect v-model="membershipProduct.product_id" :ajax="'select/products-no-wares'"></NevsSelect></td>
                                <td><NevsNumberField :decimal-places="2" v-model="membershipProduct.quantity"></NevsNumberField></td>
                                <td class="membership-action-cell"><span :title="$LANG.Get('tooltips.delete')" class="membership-button" @click="removeProduct(key)"><i
                                    class="fa-solid fa-trash"></i></span></td>
                            </tr>
                            <tr v-if="membership.products.length === 0">
                                <td colspan="3" class="membership-empty">{{ $LANG.Get('labels.noProducts') }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <NevsButton style="margin-top: 10px;" @click="addProduct()"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addProduct') }}</NevsButton>
                </div>

                <div class="membership-form-section">
                    <h2>{{ $LANG.Get('modules.sessionTypes') }}</h2>
                    <table class="membership-products-table">
                        <thead>
                            <tr>
                                <th>{{ $LANG.Get('fields.sessionType') }}</th>
                                <th style="width: 20px;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(membershipSessionType, key) in membership.session_types" :key="key">
                                <td><NevsSelect v-model="membershipSessionType.session_type_id" :ajax="'select/session-types'"></NevsSelect></td>
                                <td class="membership-action-cell"><span :title="$LANG.Get('tooltips.delete')" class="membership-button" @click="removeSessionType(key)"><i
                                    class="fa-solid fa-trash"></i></span></td>
                            </tr>
                            <tr v-if="membership.session_types.length === 0">
                                <td colspan="2" class="membership-empty">{{ $LANG.Get('labels.noSessionTypes') }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <NevsButton style="margin-top: 10px;" @click="addSessionType()"><i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addSessionType') }}</NevsButton>
                </div>
                </div>
            </div>
        </NevsCard>
    </div>
</template>

<script>
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";
import NevsMaskedField from "@/components/nevs/NevsMaskedField.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";

// Builds the default schedule: full access (00:00-23:59) on every day of week (ISO 1-7), and holidays
// (key 8) closed - no entry on a public holiday unless holiday hours are explicitly added.
function buildDefaultSchedule() {
    let schedule = {};
    for (let day = 1; day <= 7; day++) {
        schedule[day] = [{from: '00:00', to: '23:59'}];
    }
    schedule[8] = [];
    return schedule;
}

// Ensures the schedule has an interval array for every day of week (ISO 1-7) plus the holiday row (8),
// cloning incoming intervals.
function normalizeSchedule(schedule) {
    let normalized = {};
    for (let day = 1; day <= 8; day++) {
        let intervals = (schedule && Array.isArray(schedule[day])) ? schedule[day] : [];
        normalized[day] = intervals.map((interval) => ({from: interval.from, to: interval.to}));
    }
    return normalized;
}

export default {
    name: "EntityMembership",
    components: {NevsCheckbox, NevsMaskedField, NevsSelect, NevsNumberField, NevsTextField, NevsButton, NevsCard},
    data() {
        return {
            loaded: false,
            mode: this.$route.params.id === '0' ? 'add' : 'edit',
            durationTypeOptions: [
                {label: this.$LANG.Get('labels.months'), value: 'm'},
                {label: this.$LANG.Get('labels.days'), value: 'd'}
            ],
            weekdays: this.$HELPERS.GetWeekdays(this, true),
            membership: {
                id: 0,
                name: '',
                name_en: '',
                code_kpd: '',
                duration: 0,
                duration_type: 'm',
                max_entries: 0,
                price: 0,
                tax: this.$store.state.settings.DEFAULT_TAX,
                active: true,
                schedule: buildDefaultSchedule(),
                products: [],
                session_types: []
            },
            validation: {
                name: '',
                price: '',
                max_entries: ''
            }
        }
    },
    methods: {
        addInterval(dayKey) {
            this.membership.schedule[dayKey].push({from: '00:00', to: '23:59'});
        },
        removeInterval(dayKey, index) {
            this.membership.schedule[dayKey].splice(index, 1);
        },
        addProduct() {
            this.membership.products.push({
                product_id: null,
                quantity: 1
            });
        },
        removeProduct(index) {
            this.membership.products.splice(index, 1);
        },
        addSessionType() {
            this.membership.session_types.push({
                session_type_id: null
            });
        },
        removeSessionType(index) {
            this.membership.session_types.splice(index, 1);
        },
        backClick() {
            if (window.history.length === 1) {
                window.close();
            } else {
                this.$router.back();
            }
        },
        saveClick() {
            let valid = true;
            if (this.membership.name === '') {
                valid = false;
                this.validation.name = this.$LANG.Get('labels.mandatoryField');
            } else {
                this.validation.name = '';
            }
            if (this.membership.price < 0) {
                valid = false;
                this.validation.price = this.$LANG.Get('labels.errorNegativeValue');
            } else {
                this.validation.price = '';
            }
            if (this.membership.max_entries < 0) {
                valid = false;
                this.validation.max_entries = this.$LANG.Get('labels.errorNegativeValue');
            } else {
                this.validation.max_entries = '';
            }
            if (!valid) {
                return;
            }

            let payload = JSON.parse(JSON.stringify(this.membership));
            payload.products = payload.products.filter((product) => product.product_id !== null);
            payload.session_types = payload.session_types.filter((sessionType) => sessionType.session_type_id !== null);
            let method = (this.mode === 'add') ? 'post' : 'put';
            let action = (this.mode === 'add') ? 'memberships' : 'memberships/' + this.membership.id;
            let vm = this;
            this.$API.APICall(method, action, payload, (data, success) => {
                if (success) {
                    vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                    vm.backClick();
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'catalog');
        this.$store.commit('selectSubMenu', 'memberships');

        if (this.mode === 'add') {
            this.$store.commit('setBreadcrumbs', [
                {
                    label: this.$LANG.Get('modules.catalog'),
                    link: null
                },
                {
                    label: this.$LANG.Get('modules.memberships'),
                    link: '/memberships'
                },
                {
                    label: this.$LANG.Get('buttons.addMembership'),
                    link: null
                }
            ]);
            this.loaded = true;
        } else {
            let vm = this;
            this.$API.APICall('get', 'memberships/' + this.$route.params.id, {}, (data, success) => {
                if (success && data.membership !== null) {
                    vm.membership = JSON.parse(JSON.stringify(data.membership));
                    vm.membership.schedule = normalizeSchedule(vm.membership.schedule);
                    if (!Array.isArray(vm.membership.products)) vm.membership.products = [];
                    if (!Array.isArray(vm.membership.session_types)) vm.membership.session_types = [];
                    if (vm.membership.max_entries === undefined || vm.membership.max_entries === null) vm.membership.max_entries = 0;
                    vm.$store.commit('setBreadcrumbs', [
                        {
                            label: vm.$LANG.Get('modules.catalog'),
                            link: null
                        },
                        {
                            label: vm.$LANG.Get('modules.memberships'),
                            link: '/memberships'
                        },
                        {
                            label: vm.membership.name,
                            link: null
                        }
                    ]);
                    vm.loaded = true;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.membership-form {
    max-width: 900px;
    padding: 16px;
}

.membership-form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px 16px;
    align-items: start;
}

.membership-form-grid .span-2 {
    grid-column: 1 / -1;
}

.membership-form-flags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 24px;
    padding: 10px 5px 0;
}

.membership-form-sections {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid $sand-300;
}

/* Products + session types sit side by side, split by a vertical divider. */
.membership-form-columns {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 22px;
}

.membership-form-columns > .membership-form-section:first-child {
    padding-right: 24px;
}

.membership-form-columns > .membership-form-section:last-child {
    padding-left: 24px;
    border-left: 1px solid $sand-300;
}

.membership-form-section h2 {
    margin: 0 0 8px;
    font-size: 1.05em;
}

/* When the card is narrow they stack, so the divider moves to the top. */
@media (max-width: 720px) {
    .membership-form-columns {
        grid-template-columns: minmax(0, 1fr);
    }

    .membership-form-columns > .membership-form-section:first-child {
        padding-right: 0;
    }

    .membership-form-columns > .membership-form-section:last-child {
        padding-left: 0;
        margin-top: 18px;
        padding-top: 16px;
        border-left: none;
        border-top: 1px solid $sand-300;
    }
}

/* Schedule */
.schedule {
    border: 1px solid $sand-300;
    border-radius: 8px;
    overflow: hidden;
}

.schedule-day {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid $sand-200;
}

.schedule-day:last-child {
    border-bottom: none;
}

.schedule-day-name {
    width: 130px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 12px 14px;
    font-weight: 600;
    color: $ink-strong;
    background-color: $sand-100;
    border-right: 1px solid $sand-200;
}

.schedule-day-body {
    flex-grow: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
}

.schedule-interval {
    display: inline-flex;
    align-items: center;
    background-color: $peach-wash;
    border: 1px solid $sand-300;
    border-radius: 20px;
    padding: 3px 8px 3px 12px;
}

.schedule-interval-icon {
    color: $ink-muted;
    margin-right: 8px;
    font-size: 13px;
}

.schedule-interval-dash {
    margin: 0 6px;
    color: $ink-muted;
}

.schedule-interval :deep(.nevs-field) {
    margin: 0;
}

.schedule-interval :deep(.nevs-text-field) {
    text-align: center;
    padding: 4px 6px;
    border-radius: 6px;
}

.schedule-interval-remove {
    cursor: pointer;
    margin-left: 8px;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: $ink-faint;
    transition: background-color 0.15s, color 0.15s;
}

.schedule-interval-remove:hover {
    background-color: $danger-surface;
    color: $danger;
}

.schedule-empty {
    color: $ink-faint;
    font-style: italic;
    font-size: 13px;
}

.schedule-add {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: $primary;
    border: 1px dashed $sand-400;
    border-radius: 20px;
    padding: 4px 12px;
    transition: background-color 0.15s;
}

.schedule-add:hover {
    background-color: $peach-wash;
}

/* Products / session types */
.membership-products-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 4px;
}

.membership-products-table th {
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: $ink-muted;
    padding: 0 5px 4px;
}

.membership-products-table td {
    padding: 2px 5px;
    vertical-align: top;
}

.membership-products-table td.membership-action-cell {
    width: 20px;
    vertical-align: middle;
    text-align: center;
}

.membership-empty {
    color: $ink-faint;
    font-style: italic;
    font-size: 13px;
    padding: 6px 5px;
}

.membership-button {
    font-size: 14px;
    cursor: pointer;
    color: $ink-muted;
    transition: color 0.12s ease;
}

.membership-button:hover {
    color: $danger;
}

@media (max-width: 640px) {
    .membership-form-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
